import { useState } from 'react';
import { useTranslation } from '../../i18n/LocaleProvider';
import type { DsTranslationKey } from '../../i18n/catalogues/en';
import { EChart as ReactEChartsCore } from './EChart';
import * as echarts from 'echarts/core';
import { LineChart as EChartsLine } from 'echarts/charts';
import { AriaComponent, GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { SPEND_BY_MONTH } from '../../data/fixtures';
import { formatMoney, formatNumber } from '../../i18n/format';
import { ChartFrame } from './ChartFrame';
import { ChartLegend, type ChartLegendItem } from './ChartLegend';
import {
  axisTokens,
  entranceAnimation,
  formatMonth,
  seriesColor,
  tooltipTokens,
  useChartMotion,
  useChartTheme,
} from './ChartTheme';

// Only the modules this chart needs — `echarts/core` plus one chart type, the
// grid and tooltip components and the canvas renderer. The full `echarts`
// entry (and every other chart type in it) is never pulled in.
echarts.use([EChartsLine, AriaComponent, GridComponent, TooltipComponent, CanvasRenderer]);

type SpendField = 'budgeted' | 'actual';

/** Declaration order fixes the colour slot, so hiding a series never recolours
 *  the survivors. */
const SERIES: Array<{ id: string; labelKey: DsTranslationKey; field: SpendField; lineType: 'solid' | 'dashed'; symbol: string }> = [
  { id: 'budgeted', labelKey: 'ds.chart.series.budgeted', field: 'budgeted', lineType: 'solid', symbol: 'circle' },
  { id: 'actual', labelKey: 'ds.chart.series.actual', field: 'actual', lineType: 'dashed', symbol: 'diamond' },
];

const money = (value: number) => formatMoney(value, { precision: 0 });

export function LineChart() {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const motion = useChartMotion(theme);
  const [hidden, setHidden] = useState<string[]>([]);

  const months = SPEND_BY_MONTH.map((row) => row.month);
  const axis = axisTokens(theme);

  const legendItems: ChartLegendItem[] = SERIES.map((series, index) => ({
    id: series.id,
    label: t(series.labelKey),
    color: seriesColor(theme, index),
    value: money(SPEND_BY_MONTH.reduce((total, row) => total + row[series.field], 0)),
  }));

  const series = SERIES.filter((entry) => !hidden.includes(entry.id)).map((entry) => ({
    id: entry.id,
    name: t(entry.labelKey),
    type: 'line',
    // Colour slot comes from the declaration order, not the filtered order.
    itemStyle: { color: seriesColor(theme, SERIES.findIndex((s) => s.id === entry.id)) },
    // Hue is never the only cue: the two series also differ by dash pattern and
    // by symbol shape, so they stay readable in greyscale and under any form of
    // colour blindness.
    lineStyle: { width: 2, type: entry.lineType },
    symbol: entry.symbol,
    symbolSize: 8,
    data: SPEND_BY_MONTH.map((row) => row[entry.field]),
    animationDelay: motion.delay,
  }));

  const budgeted = SPEND_BY_MONTH.reduce((total, row) => total + row.budgeted, 0);
  const actual = SPEND_BY_MONTH.reduce((total, row) => total + row.actual, 0);

  return (
    <ChartFrame
      title={t('ds.chart.line.title')}
      description={t('ds.chart.line.description')}
      summary={t('ds.chart.line.summary', {
        months: formatNumber(SPEND_BY_MONTH.length),
        budgeted: money(budgeted),
        actual: money(actual),
      })}
      chartLabel={t('ds.chart.line.chartLabel', { months: formatNumber(SPEND_BY_MONTH.length) })}
      legend={<ChartLegend label={t('ds.chart.line.legend')} items={legendItems} hidden={hidden} onToggle={(id) => setHidden((prev) => (prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]))} shape="line" />}
      empty={series.length === 0 ? t('ds.chart.line.empty') : undefined}
      table={{
        caption: t('ds.chart.line.tableCaption'),
        columns: [t('ds.chart.line.columnMonth'), t('ds.chart.series.budgeted'), t('ds.chart.series.actual')],
        rows: SPEND_BY_MONTH.map((row) => [formatMonth(row.month), money(row.budgeted), money(row.actual)]),
      }}
    >
      <ReactEChartsCore
        echarts={echarts}
        style={{ width: '100%', height: '100%' }}
        option={{
          ...entranceAnimation(motion),
          // Decals give every series a texture as well as a hue, which is the
          // colourblind-safe layer the design system requires. `aria.enabled`
          // is what switches them on (echarts/lib/visual/aria.js returns early
          // without it); `aria.label` is off because this frame supplies the
          // accessible name and summary itself.
          aria: { enabled: true, label: { enabled: false }, decal: { show: true } },
          grid: { left: 4, right: 16, top: 16, bottom: 4, containLabel: true },
          tooltip: { trigger: 'axis', ...tooltipTokens(theme, money) },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: months,
            ...axis,
            splitLine: { show: false },
            axisLabel: { ...axis.axisLabel, hideOverlap: true, formatter: formatMonth },
          },
          yAxis: { type: 'value', ...axis, axisLabel: { ...axis.axisLabel, formatter: money } },
          series,
        }}
      />
    </ChartFrame>
  );
}
